import { productBrand } from "@/constants/Brand"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import { backOrReplace } from "@/hooks/useBackOrReplace"
import { router } from "expo-router"
import { ArrowLeft } from "lucide-react-native"
import React from "react"
import { Animated, Image, Platform, Text, TouchableOpacity, View } from "react-native"

export const COLLAPSIBLE_HEADER_EXPANDED_HEIGHT = 176
export const COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING = COLLAPSIBLE_HEADER_EXPANDED_HEIGHT + 20
export const COLLAPSIBLE_HEADER_COMPACT_HEIGHT = 78
const COLLAPSE_DISTANCE = 74
const NAV_ROW_HEIGHT = 44
const SIDE_SLOT_WIDTH = 92
const ACTION_BUTTON_SIZE = 40
const AVATAR_BUTTON_SIZE = 36
const AVATAR_IMAGE_SIZE = 32
const HEADER_ACTION_GAP = 8
const HEADER_ACTION_HIT_SLOP = { top: 10, right: 10, bottom: 10, left: 10 }

type CollapsibleScreenHeaderProps = {
  scrollY: Animated.Value
  eyebrow: string
  title: string
  subtitle: string
  accentColor?: string
  backFallbackHref?: Parameters<typeof backOrReplace>[0]
  backLabel?: string
  rightAction?: React.ReactNode
  statusText?: string
  testID?: string
}

function BrandPill({ accentColor, testID }: { accentColor: string; testID?: string }) {
  return (
    <View
      testID={testID}
      style={{
        alignSelf: "center",
        backgroundColor: `${accentColor}20`,
        borderColor: `${accentColor}70`,
        borderRadius: 999,
        borderWidth: 1,
        paddingHorizontal: 16,
        paddingVertical: 6,
      }}
    >
      <Text
        numberOfLines={1}
        style={{ color: accentColor, fontSize: 14, fontWeight: "800", lineHeight: 18 }}
      >
        {productBrand.productName}
      </Text>
    </View>
  )
}

function getAvatarLabel(displayName: string) {
  const trimmedName = displayName.trim().replace(/@.*/, "")

  if (trimmedName.length > 0) {
    const initials = trimmedName
      .split(/\s|[._-]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("")

    return initials || trimmedName[0]?.toUpperCase() || "EQ"
  }

  return "EQ"
}

export function CollapsibleScreenHeader({
  scrollY,
  eyebrow,
  title,
  subtitle,
  accentColor = Colors.dark.primary,
  backFallbackHref,
  backLabel = "Back",
  rightAction,
  statusText,
  testID = "collapsible-screen-header",
}: CollapsibleScreenHeaderProps) {
  const auth = useAuth()
  const hasBackButton = Boolean(backFallbackHref)
  const accountLabel = auth.profile?.display_name ?? auth.user?.email?.split("@")[0] ?? "EQ builder"
  const avatarLabel = getAvatarLabel(accountLabel)
  const avatarUrl = auth.profile?.avatar_url
  const height = scrollY.interpolate({
    inputRange: [0, COLLAPSE_DISTANCE],
    outputRange: [COLLAPSIBLE_HEADER_EXPANDED_HEIGHT, COLLAPSIBLE_HEADER_COMPACT_HEIGHT],
    extrapolate: "clamp",
  })
  const titleSize = scrollY.interpolate({
    inputRange: [0, COLLAPSE_DISTANCE],
    outputRange: [31, 20],
    extrapolate: "clamp",
  })
  const titleLineHeight = scrollY.interpolate({
    inputRange: [0, COLLAPSE_DISTANCE],
    outputRange: [38, 25],
    extrapolate: "clamp",
  })
  const expandedContentOpacity = scrollY.interpolate({
    inputRange: [0, 48, COLLAPSE_DISTANCE],
    outputRange: [1, 0.28, 0],
    extrapolate: "clamp",
  })
  const expandedContentTranslate = scrollY.interpolate({
    inputRange: [0, COLLAPSE_DISTANCE],
    outputRange: [0, -14],
    extrapolate: "clamp",
  })
  const headerActionOpacity = scrollY.interpolate({
    inputRange: [0, 36, COLLAPSE_DISTANCE],
    outputRange: [1, 0.25, 0],
    extrapolate: "clamp",
  })
  const topPadding = Platform.OS === "web" ? 10 : 8

  const leftSlot = hasBackButton ? (
    <TouchableOpacity
      accessibilityLabel={backLabel}
      accessibilityRole="button"
      activeOpacity={0.72}
      hitSlop={HEADER_ACTION_HIT_SLOP}
      testID="collapsible-header-back-button"
      style={{
        alignItems: "center",
        backgroundColor: Colors.dark.surfaceSecondary,
        borderRadius: 999,
        height: ACTION_BUTTON_SIZE,
        justifyContent: "center",
        width: ACTION_BUTTON_SIZE,
      }}
      onPress={() => backOrReplace(backFallbackHref)}
    >
      <ArrowLeft color={accentColor} size={21} />
    </TouchableOpacity>
  ) : (
    <View
      testID="collapsible-header-left-spacer"
      style={{ height: ACTION_BUTTON_SIZE, width: ACTION_BUTTON_SIZE }}
    />
  )

  const profileAvatar = (
    <TouchableOpacity
      accessibilityLabel="Open account settings"
      accessibilityRole="button"
      activeOpacity={0.72}
      hitSlop={HEADER_ACTION_HIT_SLOP}
      testID="collapsible-header-profile-button"
      style={{
        alignItems: "center",
        backgroundColor: Colors.dark.surfaceSecondary,
        borderColor: `${Colors.dark.primary}55`,
        borderRadius: 999,
        borderWidth: 1,
        height: AVATAR_BUTTON_SIZE,
        justifyContent: "center",
        width: AVATAR_BUTTON_SIZE,
      }}
      onPress={() => router.push("/auth")}
    >
      {avatarUrl ? (
        <Image
          accessibilityIgnoresInvertColors
          source={{ uri: avatarUrl }}
          style={{
            borderRadius: AVATAR_IMAGE_SIZE / 2,
            height: AVATAR_IMAGE_SIZE,
            width: AVATAR_IMAGE_SIZE,
          }}
        />
      ) : (
        <Text style={{ color: accentColor, fontSize: 12, fontWeight: "800" }}>{avatarLabel}</Text>
      )}
    </TouchableOpacity>
  )

  return (
    <Animated.View
      pointerEvents="box-none"
      testID={testID}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        height,
        paddingHorizontal: 24,
        paddingTop: topPadding,
        paddingBottom: 12,
        backgroundColor: Colors.dark.surface,
        borderBottomWidth: 1,
        borderBottomColor: Colors.dark.border,
        shadowColor: "#000000",
        shadowOpacity: 0.18,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 10 },
        overflow: "hidden",
      }}
    >
      <View
        style={{
          alignItems: "center",
          flexDirection: "row",
          height: NAV_ROW_HEIGHT,
          justifyContent: "space-between",
        }}
      >
        <View style={{ alignItems: "flex-start", width: SIDE_SLOT_WIDTH }}>{leftSlot}</View>
        <View style={{ alignItems: "center", justifyContent: "center" }}>
          <BrandPill accentColor={accentColor} testID="collapsible-header-compact-logo" />
        </View>
        <View
          style={{
            alignItems: "center",
            flexDirection: "row",
            gap: HEADER_ACTION_GAP,
            justifyContent: "flex-end",
            width: SIDE_SLOT_WIDTH,
          }}
        >
          {rightAction ? (
            <Animated.View
              pointerEvents="box-none"
              style={{ opacity: headerActionOpacity, transform: [{ scale: headerActionOpacity }] }}
            >
              {rightAction}
            </Animated.View>
          ) : null}
          {profileAvatar}
        </View>
      </View>

      <Animated.View
        pointerEvents="none"
        style={{
          marginTop: 12,
          minWidth: 0,
          opacity: expandedContentOpacity,
          transform: [{ translateY: expandedContentTranslate }],
        }}
      >
        <View style={{ flexDirection: "row", gap: 10, minWidth: 0 }}>
          <Text
            numberOfLines={1}
            style={{
              color: accentColor,
              flex: 1,
              fontSize: 13,
              fontWeight: "800",
              lineHeight: 17,
            }}
          >
            {eyebrow}
          </Text>
          {statusText ? (
            <Text
              numberOfLines={1}
              style={{
                color: accentColor,
                flexShrink: 1,
                fontSize: 12,
                fontWeight: "800",
                lineHeight: 16,
                maxWidth: 128,
                textAlign: "right",
              }}
            >
              {statusText}
            </Text>
          ) : null}
        </View>
        <Animated.Text
          numberOfLines={1}
          testID="collapsible-header-title"
          style={{
            color: Colors.dark.textPrimary,
            fontSize: titleSize,
            fontWeight: "800",
            lineHeight: titleLineHeight,
            marginTop: 6,
          }}
        >
          {title}
        </Animated.Text>
        <Text
          numberOfLines={2}
          testID="collapsible-header-subtitle"
          style={{
            color: Colors.dark.textSecondary,
            fontSize: 15,
            fontWeight: "600",
            lineHeight: 20,
            marginTop: 4,
          }}
        >
          {subtitle}
        </Text>
      </Animated.View>
    </Animated.View>
  )
}
